import { type ReactNode, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@app/store/store';
import { invokeLogout } from '@features/authentication/store/authentication.thunks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@shared/components/ui';
import { AccountDetailsPanel } from '@shared/components/ui/custom/AccountDetailsPanel';
import { cn } from '@shared/utils';

type AccountMenuItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export const AppAccountMenu = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.authentication);
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileDetailsOpen, setIsProfileDetailsOpen] = useState(false);

  const first = user?.firstName?.[0]?.toUpperCase() ?? '';
  const last = user?.lastName?.[0]?.toUpperCase() ?? '';
  const initials = `${first}${last}` || 'NA';

  const accountMenuItems: AccountMenuItem[] = [
    {
      key: 'account-details',
      label: 'Account',
      onClick: () => setIsProfileDetailsOpen(true),
    },
    {
      key: 'logout',
      label: 'Logout',
      onClick: () => {
        void dispatch(invokeLogout());
      },
    },
  ];

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-center w-16 h-16 border-l border-border ml-auto cursor-pointer',
              'hover:bg-accent',
              isOpen && 'bg-accent text-foreground',
            )}
            data-testid="account-menu-trigger"
          >
            <span className="text-base font-medium text-zinc-50 leading-[1.57]">{initials}</span>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="bottom"
          align="start"
          className="min-w-[200px] p-1 -translate-y-px rounded-md border border-muted-border bg-popover"
        >
          {accountMenuItems.map((item) => (
            <DropdownMenuItem
              key={item.key}
              disabled={!!item.disabled}
              onClick={() => {
                item.onClick?.();
                setIsOpen(false);
              }}
              className={cn('flex items-center gap-2 cursor-pointer', item.disabled && 'opacity-50 cursor-not-allowed')}
              data-testid={`account-menu-option-${item.key}`}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {isProfileDetailsOpen && user && (
        <AccountDetailsPanel isOpen={isProfileDetailsOpen} onOpenChange={setIsProfileDetailsOpen} />
      )}
    </>
  );
};
